import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

const CONTACT_PHONE_DISPLAY = '+1 (614) 579-0661';
const CONTACT_PHONE_LINK = 'tel:+16145790661';
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

const primaryActionSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 48,
  paddingX: 3,
  paddingY: 1.5,
  borderRadius: 1,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  fontWeight: 700,
  textDecoration: 'none',
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    backgroundColor: 'primary.dark',
    textDecoration: 'none',
  },
};

const secondaryActionSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 48,
  paddingX: 3,
  paddingY: 1.5,
  borderRadius: 1,
  border: 1,
  borderColor: 'primary.main',
  color: 'primary.main',
  fontWeight: 700,
  textDecoration: 'none',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'action.hover',
    textDecoration: 'none',
  },
};

const textActionSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 48,
  paddingX: 1,
  color: 'primary.main',
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
  },
};

const statusContent = {
  submitted: {
    title: 'Form submitted successfully',
    message:
      'Thank you for reaching out. You should hear back from us within 1-2 business days.',
    primaryLabel: 'Back home',
    secondaryLabel: 'Submit another request',
  },
  error: {
    title: 'We couldn\'t send your request',
    message:
      'Please resubmit the form, or call us directly if your project is urgent.',
    primaryLabel: 'Resubmit form',
    secondaryLabel: 'Back home',
  },
};

const SubmissionStatus = ({ status, errorMessage }) => {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, {
    defaultMatches: false,
  });
  const content = statusContent[status];
  const statusRef = useRef(null);

  useEffect(() => {
    if (!isMobile || status !== 'submitted' || !statusRef.current) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const element = statusRef.current;

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const centeredTop =
        window.scrollY + rect.top - (window.innerHeight - rect.height) / 2;

      window.scrollTo({
        top: Math.max(centeredTop, 0),
        behavior: 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isMobile, status]);

  if (!content) {
    return null;
  }

  return (
    <Box
      ref={statusRef}
      textAlign={'center'}
      width={'100%'}
      sx={{
        minHeight: { xs: '55vh', md: 'auto' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        scrollMarginTop: { xs: 24, md: 0 },
      }}
    >
      <Box width={'100%'}>
        <Typography variant={'h3'} sx={{ fontWeight: 700 }} gutterBottom>
          {content.title}
        </Typography>
        <Typography variant={'subtitle1'} color={'textSecondary'}>
          {content.message}{' '}
          If it&apos;s urgent, call us at{' '}
          <Link href={CONTACT_PHONE_LINK} underline={'hover'}>
            {CONTACT_PHONE_DISPLAY}
          </Link>
          .
        </Typography>
        {status === 'error' && errorMessage ? (
          <Typography
            variant={'body2'}
            color={'textSecondary'}
            sx={{ marginTop: 2 }}
          >
            Details: {errorMessage}
          </Typography>
        ) : null}
        <Box
          display={'flex'}
          justifyContent={'center'}
          flexWrap={'wrap'}
          gap={2}
          marginTop={3}
        >
          {status === 'submitted' ? (
            <>
              <Link
                underline={'none'}
                component={'a'}
                href={'/'}
                sx={primaryActionSx}
              >
                {content.primaryLabel}
              </Link>
              <Link
                underline={'none'}
                component={'a'}
                href={'/contact'}
                sx={secondaryActionSx}
              >
                {content.secondaryLabel}
              </Link>
            </>
          ) : (
            <>
              <Link
                underline={'none'}
                component={'a'}
                href={'/contact'}
                sx={primaryActionSx}
              >
                {content.primaryLabel}
              </Link>
              <Link
                underline={'none'}
                component={'a'}
                href={'/'}
                sx={secondaryActionSx}
              >
                {content.secondaryLabel}
              </Link>
            </>
          )}
          {isMobile ? (
            <Link
              underline={'none'}
              component={'a'}
              href={CONTACT_PHONE_LINK}
              sx={textActionSx}
            >
              Call us now
            </Link>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

SubmissionStatus.propTypes = {
  status: PropTypes.oneOf(['submitted', 'error']),
  errorMessage: PropTypes.string,
};

SubmissionStatus.defaultProps = {
  status: undefined,
  errorMessage: '',
};

export default SubmissionStatus;